<template>
  <div class="donapp-dialog-container flex-center">
    <div class="donapp-dialog-background flex-center" @click.stop.prevent="onClose"></div>
    <div :class="{'dialog-container': true, wide: wide}">
      <div class="dialog">
        <div :class="['dialog-header', 'gradient-' + theme]">
          <h3>{{ header }}</h3>
          <h2>{{ title }}</h2>
        </div>
        <div class="dialog-body">
          <slot></slot>
        </div>
        <div class="dialog-footer" v-if="footer">
          <button :class="theme" @click.stop.prevent="onClose">{{$t('labels.cancel')}}</button>
          <button :class="{[theme]: true, disabled: errors.any() || disableOk}" @click.stop.prevent="onSubmit">{{$t('labels.ok')}}</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
const themes = ['user', 'merchant', 'npo']

export default {
  name: 'donapp-dialog',
  inject: ['$validator'],
  props: {
    header: {
      type: String,
      default: 'test'
    },
    title: {
      type: String,
      default: 'Test'
    },
    theme: {
      type: String,
      default: 'user',
      validator: function(value) {
        return themes.indexOf(value) > -1
      }
    },
    value: {
      type: String,
      required: false
    },
    footer: {
      type: Boolean,
      default: true
    },
    wide: {
      type: Boolean,
      default: false
    },
    disableOk: {
      type: Boolean,
      default: false
    }
  },
  created() {
    this.hideBodyOverflow(true)
    this.setEscapeEvent()
  },
  methods: {
    // Misc methods that have nothing to do with dialog stuff.

    /**
     * Hides the overflow of the body when the dialog is active.
     *
     * @param  {Boolean}
     */
    hideBodyOverflow(show) {
      const bodyEl = document.querySelector('body')

      if (show === true) {
        bodyEl.style.overflow = 'hidden'

        return
      }

      bodyEl.style.overflow = ''
    },

    /**
     * Close the dialog if the escape key is pressed.
     */
    setEscapeEvent() {
      document.addEventListener('keydown', event => {
        if (event.keyCode === 27 || event.key === 'Escape') {
          this.onClose()
        }
      })
    },
    /**
     * Emit a close event so the developer can close the dialog as they
     * see fit.
     */
    onClose() {
      this.hideBodyOverflow(false)
      this.$emit('close')
    },
    /**
     * Emit a close event so the developer can close the dialog as they
     * see fit.
     */
    onSubmit() {
      if (!this.disableOk) {
        this.$emit('submit')
      }
    }
  }
}
</script>

<style scoped lang="scss">
@import '../../scss/config';

*,
*::before,
*::after {
  box-sizing: inherit;
}

div,
h2,
h3,
h4,
button {
  margin: 0;
  padding: 0;
  border: 0;
  font-size: 100%;
  font: inherit;
  vertical-align: baseline;
}

button {
  border-radius: 2px;
  cursor: pointer;
  display: inline-block;
  font-size: 16px;
  padding: 0 16px;
  outline: none;
  user-select: none;
}

button.disabled {
  color: $dividerGrey !important;
  cursor: default;
}

.donapp-dialog-container,
.donapp-dialog-background {
  overflow-y: auto;
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}

.donapp-dialog-container {
  line-height: 1.5;
  z-index: 9998;
}

.donapp-dialog-background {
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 9999;
}

.dialog-faint {
  opacity: 0.75;
}

.dialog-container {
  background-color: #fff;
  border-radius: 2px;
  cursor: initial;
  position: relative;
  user-select: text;
  width: 350px;
  z-index: 10001;
  user-select: none;
}

.dialog-container.wide {
  width: 410px;
}

.dialog {
  position: relative;
}

.dialog-header {
  border-top-left-radius: 2px;
  border-top-right-radius: 2px;
  color: #fff;
  padding: 16px 17px;
}

.dialog-header h2,
.dialog-header h3 {
  cursor: default;
}

.dialog-header h2.dialog-faint,
.dialog-header h3.dialog-faint {
  cursor: pointer;
}

.dialog-header h2 {
  font-size: 20px;
  font-weight: bold;
  line-height: 24px;
  margin-top: 3px;
}

.dialog-header h3 {
  font-size: 1.125rem;
  font-weight: 300;
}

.dialog-body {
  padding: 17px;
}

.dialog-date {
  display: flex;
}

.dialog-date .dialog-arrows.left {
  display: flex;
  align-items: center;
  justify-content: flex-start;
}

.dialog-current-date {
  cursor: default;
  text-align: center;
}

.dialog-date .dialog-arrows.right {
  display: flex;
  align-items: center;
  justify-content: flex-end;
}

.dialog-date > div {
  width: 33.333%;
}

.dialog-date h4 {
  font-size: 14px;
  font-weight: 500;
}

.dialog-arrows {
  cursor: pointer;
  padding: 0 8px;
}

.dialog-body table {
  border-spacing: 2px;
  border-collapse: separate;
  cursor: default;
  font-size: 12px;
  margin-top: 8px;
  table-layout: fixed;
  text-align: center;
  width: 100%;
}

.dialog-body table thead {
  color: #757575;
}

.dialog-body table thead td,
.dialog-body table thead td {
  cursor: default !important;
}

.dialog-body tr {
  height: 43px;
  vertical-align: middle;
}

.dialog-body td {
  border-radius: 50%;
  cursor: pointer;
  vertical-align: middle;
  transition: background-color 0.15s;
  width: 43px;
}

.dialog-body td:focus {
  outline: none;
}

.dialog-body tbody td:hover {
  background-color: rgba(0, 0, 0, 0.1);
}

.dialog-body td:empty {
  background-color: transparent !important;
  cursor: default;
}

.dialog-body td.current-day {
  font-weight: bold;
}

.dialog-body td.disabled {
  color: #c5c5c5;
  cursor: default;
  pointer-events: none;
}

.dialog-body td.selected {
  border-radius: 50%;
  color: #fff;
  cursor: pointer;
}

.hidden-footer {
  height: 378px;
}

.dialog-footer {
  position: relative;
  display: flex;
  justify-content: flex-end;
  padding: 17px;
}

.dialog-footer button {
  background-color: transparent;
  border: 1px solid transparent;
  box-shadow: none;
}

.dialog-fade-enter-active,
.dialog-fade-leave-active {
  transition: opacity 0.15s;
}

.dialog-fade-enter,
.dialog-fade-leave-to {
  opacity: 0;
}

button.merchant {
  color: $merchantStart;
}

button.npo {
  color: $npoStart;
}

button.user {
  color: $userStart;
}
</style>



// WEBPACK FOOTER //
// src/components/shared/Dialog.vue